import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Typography } from 'antd'
import { isApiError, startMfaSetup, tokenStore } from '../api'
import { useSessionValue } from '../hooks/use-session-value'

type QrState =
  | { status: 'loading' }
  | { status: 'ready'; qrCode: string }
  | { status: 'error'; message: string }

const BOX_CLASSES =
  'w-[120px] h-[120px] bg-white rounded-xl border border-brand-border-light/80 flex items-center justify-center p-3 shadow-sm select-none text-center text-xs text-brand-muted'

/**
 * Fetches the TOTP QR code from `POST /auth/mfa/setup` (using the JWT from the
 * OTP step) and renders it. Also caches the `otpauth://` URL so the manual-key
 * step can show the secret. Renders loading / error / unauthorized fallbacks in
 * place of the QR box.
 */
export function QrCodePanel() {
  const navigate = useNavigate()
  const token = useSessionValue(() => tokenStore.getAccessToken())
  const [state, setState] = React.useState<QrState>({ status: 'loading' })

  React.useEffect(() => {
    if (!token) return // unauthorized — handled in render below

    const controller = new AbortController()
    startMfaSetup(token, controller.signal)
      .then((data) => {
        tokenStore.setOtpauthUrl(data.otpauthUrl)
        setState({ status: 'ready', qrCode: data.qrCodeBase64 })
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          isApiError(err) && err.isUnauthorized
            ? 'Your session has expired. Please sign-in again.'
            : isApiError(err)
              ? err.message
              : "Couldn't load the QR code."
        setState({ status: 'error', message })
      })

    return () => controller.abort()
  }, [token])

  if (!token) {
    return (
      <div className={BOX_CLASSES}>
        <span>
          Session expired.{' '}
          <Typography.Link
            onClick={() => navigate({ to: '/login' })}
            className="font-semibold"
          >
            Sign in again
          </Typography.Link>
        </span>
      </div>
    )
  }

  if (state.status === 'error') {
    return <div className={BOX_CLASSES}>{state.message}</div>
  }

  if (state.status === 'loading') {
    return <div className={BOX_CLASSES}>Generating QR code…</div>
  }

  return (
    <img
      src={state.qrCode}
      alt="Authenticator setup QR code"
      width={120}
      height={120}
      className="w-[120px] h-[120px] bg-white rounded-xl border border-brand-border-light/80 p-3 shadow-sm"
    />
  )
}
